/**
 * Shared HTML Utilities
 * 
 * Utilities for safely escaping and manipulating HTML content
 * 
 * @module shared/htmlUtils
 */

/**
 * Escape HTML special characters for safe display in HTML content
 * 
 * @param text - Text to escape
 * @returns HTML-safe string
 * 
 * @example
 * escapeHtml('<script>alert("xss")</script>')
 * // '&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;'
 */
export function escapeHtml(text: string | number | null | undefined): string {
  if (text == null) {
    return '';
  }
  
  const str = String(text);
  return str.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return char;
    }
  });
}

/**
 * Escape text for safe use in HTML attributes
 * Identical to escapeHtml but semantically named for attribute context
 * 
 * @param text - Text to escape for attribute value
 * @returns HTML attribute-safe string
 * 
 * @example
 * escapeAttr('value with "quotes"') // 'value with &quot;quotes&quot;'
 */
export function escapeAttr(text: string | number | null | undefined): string {
  return escapeHtml(text);
}

/**
 * Decode HTML entities in a string
 * 
 * Uses browser-native decoding via a temporary textarea element.
 * Works in content scripts and web pages where DOM is available.
 * 
 * @param text - Text with potential HTML entities
 * @returns Decoded text with entities converted to characters
 * 
 * @example
 * unescapeHtml('Wyatt &amp; Ash 5&#39;3&quot;') // "Wyatt & Ash 5'3\""
 */
export function unescapeHtml(text: string | null | undefined): string {
  if (text == null || text === '') {
    return '';
  }
  
  // Fast path: if no ampersand, no entities to decode
  if (!text.includes('&')) {
    return text;
  }
  
  // Use browser's native HTML parsing to decode entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Capitalize the first letter of a string
 * 
 * @param text - Text to capitalize
 * @returns Text with first letter capitalized
 * 
 * @example
 * capitalize('hello world') // 'Hello world'
 */
export function capitalize(text: string): string {
  if (!text || text.length === 0) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Truncate text to a maximum length with ellipsis
 * 
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with '...' if needed
 * 
 * @example
 * truncate('Long text here', 10) // 'Long text...'
 */
export function truncate(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}
